/**
 * Sparkline (P282.3) -- a self-contained inline trend line over a numeric
 * series, normalised to the series' own range so the SHAPE reads for any
 * unit (%, count, hours). A shared primitive (the telemetry MetricSparkline
 * lives in twin/ and the module boundary forbids framework importing it).
 * Renders nothing meaningful below 2 points ("--").
 */

/** Normalise to [0, 1] against the series' own min..max (flat -> mid-line). */
export function normaliseRange(points: number[]): number[] {
  if (points.length === 0) return [];
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min;
  if (span <= 0) return points.map(() => 0.5);
  return points.map((v) => (v - min) / span);
}

export function Sparkline({
  points,
  tint = "stroke-brand-brass",
  testid,
}: {
  points: number[];
  tint?: string;
  testid?: string;
}): JSX.Element {
  const W = 64;
  const H = 16;
  const yfracs = normaliseRange(points);
  if (yfracs.length < 2) {
    return <span className="block text-[10px] text-ink-muted">--</span>;
  }
  const step = W / (yfracs.length - 1);
  const path = yfracs
    .map((f, i) => `${(i * step).toFixed(1)},${(H - f * (H - 2) - 1).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      data-testid={testid}
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      aria-hidden="true"
      className="block"
    >
      <polyline
        points={path}
        fill="none"
        strokeWidth="1.5"
        strokeLinejoin="round"
        className={tint}
      />
    </svg>
  );
}
